/* TikTok owns the media; profile cards and counts are rendered by the theme. */
(() => {
  if (customElements.get('ttt-social-feed')) return;
  let embedNumber = 0;

  class TTTSocialFeed extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.panel = this.querySelector('[data-social-panel="tiktok"]');
      if (!this.panel) return;
      this.controller = new AbortController();
      this.classList.add('is-enhanced');
      this.panel.querySelector('[data-social-retry]').addEventListener('click', () => this.mount(), {
        signal: this.controller.signal,
      });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.mount();
        }, { rootMargin: '250px 0px' });
        // Observe the player, so the cards can be read before requesting media.
        this.observer.observe(this.panel);
      } else {
        this.mount();
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.cleanup?.();
      if (this.panel) {
        delete this.panel.dataset.state;
        this.panel.removeAttribute('aria-busy');
        this.panel.querySelector('[data-social-embed]').replaceChildren();
      }
    }

    mount() {
      if (!this.isConnected || !this.controller || this.panel.dataset.state === 'loading') return;
      this.cleanup?.();
      const panel = this.panel;
      const username = panel.dataset.username;
      const valid = /^[a-zA-Z0-9._]{1,24}$/.test(username);
      const status = panel.querySelector('[data-social-status]');
      const retry = panel.querySelector('[data-social-retry]');
      const slot = panel.querySelector('[data-social-embed]');
      const signal = this.controller.signal;
      let timer;
      let removeListener;
      this.cleanup = () => { clearTimeout(timer); removeListener?.(); };
      const fail = () => {
        this.cleanup();
        if (!this.isConnected || signal.aborted) return;
        panel.dataset.state = 'error';
        panel.removeAttribute('aria-busy');
        slot.replaceChildren();
        status.textContent = 'The videos could not load. You can still watch them on TikTok.';
        status.hidden = false;
        retry.hidden = !valid;
      };
      if (!valid) { fail(); return; }
      panel.dataset.state = 'loading';
      panel.setAttribute('aria-busy', 'true');
      status.textContent = 'Loading TikTok…';
      status.hidden = false;
      retry.hidden = true;
      const iframe = document.createElement('iframe');
      iframe.name = `__tt_embed__${panel.id}_${++embedNumber}`;
      iframe.title = `TikTok videos from @${username}`;
      iframe.className = 'ttt-social__tiktok-player';
      iframe.allow = 'fullscreen';
      iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation');
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      const onMessage = event => {
        if (event.origin !== 'https://www.tiktok.com' || event.source !== iframe.contentWindow || !slot.contains(iframe)) return;
        let data;
        try { data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data; }
        catch { return; }
        if (!data || data.signalSource !== iframe.name || !Number.isFinite(data.height)
          || data.height < 200 || data.height > 1600) return;
        iframe.style.height = `${Math.ceil(data.height)}px`;
        panel.dataset.state = 'mounted';
        panel.removeAttribute('aria-busy');
        status.hidden = true;
        retry.hidden = true;
        clearTimeout(timer);
      };
      window.addEventListener('message', onMessage, { signal });
      removeListener = () => window.removeEventListener('message', onMessage);
      timer = setTimeout(fail, 20000);
      iframe.src = `https://www.tiktok.com/embed/@${username}`;
      slot.replaceChildren(iframe);
    }
  }
  customElements.define('ttt-social-feed', TTTSocialFeed);
})();
